import {
  Component,
  EventEmitter,
  HostListener,
  Inject,
  OnInit,
  Output,
  ViewEncapsulation,
} from "@angular/core";
import { FormArray, FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Router } from "@angular/router";

import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";

import { Observable, Subject } from "rxjs";
import { select, Store } from "@ngrx/store";
import { AppState, sitesStateIsLoading } from "src/app/store/reducers";
import {
  createPriceSign,
  updatePriceSign,
} from "src/app/store/actions/sites/sites.actions";

import { TranslateService } from "@ngx-translate/core";
import { SnackbarService } from "src/app/shared/services/snackbar/snackbar.service";

import {
  PriceSignType,
  SitePriceSignDTO,
} from "src/app/models/Sites/sitePriceSignDTO";
import { SiteGradeDTO } from "src/app/models/Sites/siteGradeDTO";
import {
  Dialog,
  GuardConfirmationDialogData,
} from "src/app/models/dialog/dialog.model";
import { SnackbarType } from "src/app/models/snackbar/snackbar.model";
import {
  CommunicationMethodTypeCode,
  PROTOCOL_FORM_PARAMS,
} from "src/app/models/protocol/protocol.model";
import { isAuthenticated } from "src/app/shared/utils/authenticationUtils";
import { FormUtils } from "src/app/shared/utils/formUtils";
import {
  setCommunicationMethodData,
  setProtocolFormValues,
} from "src/app/shared/utils/protocolFormUtils";

import { CanComponentDeactivate } from "src/app/shared";
import { ConfirmationDialogComponent } from "src/app/shared/modules/dialogs/confirmation-dialog/confirmation-dialog.component";
import { RolePermissionsService } from "src/app/shared/services/role-permissions/role-permissions.service";
import { filter, switchMap, take, takeUntil } from "rxjs/operators";
import { SiteProductDTO } from "src/app/models/Products/productDTO";
import {
  PricePolePoint,
  Segment,
} from "../../../../models/Sites/sitePriceSignDTO";

const PRICE_SIGN_FORM_PARAMS = {
  enabled: "enabled",
  priceSignFccCode: "priceSignFccCode",
  serialNumber: "serialNumber",
  description: "description",
  protocolTypeCode: "protocolTypeCode",
  pricePolePoints: "pricePolePoints",
  pricePolePointID: "pricePolePointID",
  signNo: "signNo",
  manualMode: "manualMode",
  segments: "segments",
  segmentNo: "segmentNo",
  productNo: "productNo",
  modeNo: "modeNo",
  protocol: "protocol",
};

@Component({
  selector: "app-warehouse-form-dialog",
  templateUrl: "./price-sign-form-dialog.component.html",
  styleUrls: ["./price-sign-form-dialog.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class PriceSignFormDialogComponent
  implements OnInit, CanComponentDeactivate
{
  @Output() getPriceSignForm: EventEmitter<FormGroup> =
    new EventEmitter<FormGroup>();

  isLoading$: Observable<boolean>;
  protected destroy$ = new Subject<void>();

  // Permissions
  public canWrite$: Observable<boolean>;
  canWrite: boolean = false;

  siteCode: string;
  currentPriceSign: SitePriceSignDTO;

  priceSignTypeList: PriceSignType[];
  siteProductList: SiteProductDTO[];

  priceSignDataForm: FormGroup;

  isCreate: boolean = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<PriceSignFormDialogComponent>,
    private fb: FormBuilder,
    private router: Router,
    private store: Store<AppState>,
    private snackbarService: SnackbarService,
    private translate: TranslateService,
    public rolePermissionsService: RolePermissionsService,
  ) {
    this.isLoading$ = this.store.pipe(select(sitesStateIsLoading));
  }

  ngOnInit(): void {
    this.setDialogData();
    this.createPriceSignForm();
    this.setFormValues(this.currentPriceSign);
    if (!this.isCreate) {
      this.setPricePolePointsFormArray(this.currentPriceSign);
    }
    this.checkPermissions();
  }

  setDialogData() {
    this.isCreate = this.data.isCreate;
    this.siteCode = this.data.siteCode;
    this.currentPriceSign = this.data.currentPriceSign;
    this.priceSignTypeList = this.data.priceSignTypeList;
    this.siteProductList = this.data.siteProductList;
  }

  createPriceSignForm() {
    this.priceSignDataForm = this.fb.group({
      [PRICE_SIGN_FORM_PARAMS.enabled]: [true],
      [PRICE_SIGN_FORM_PARAMS.priceSignFccCode]: [
        null,
        [Validators.required, Validators.min(1), Validators.max(255)],
      ],
      [PRICE_SIGN_FORM_PARAMS.serialNumber]: [null, Validators.maxLength(50)],
      [PRICE_SIGN_FORM_PARAMS.description]: [
        null,
        [
          Validators.required,
          FormUtils.noWhitespaceValidator,
          Validators.maxLength(45),
        ],
      ],
      [PRICE_SIGN_FORM_PARAMS.protocolTypeCode]: [null, [Validators.required]],
      [PRICE_SIGN_FORM_PARAMS.protocol]: this.fb.group({}),
      [PRICE_SIGN_FORM_PARAMS.pricePolePoints]: this.fb.array(
        [this.createPricePolePointFormGroup()],
        [Validators.required],
      ),
    });
    this.subscribeFormChanges(this.priceSignDataForm);
  }

  get pricePolePoints(): FormArray {
    return this.priceSignDataForm.get(PRICE_SIGN_FORM_PARAMS.pricePolePoints) as FormArray;
  }

  checkPermissions(): void {
    this.rolePermissionsService["roles$"]
      .pipe(
        filter((roles) => roles.length > 0),
        take(1),
        switchMap(() =>
          this.rolePermissionsService.hasPermission("SITE_MANAGEMENT", "WRITE"),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe((canEdit) => {
        this.canWrite = canEdit;
        if (!canEdit) {
          this.setFormValues(this.currentPriceSign);
          setTimeout(() => {
            this.disableFormControls(this.priceSignDataForm);
          });
        }
      });

    this.canWrite$ = this.rolePermissionsService.hasPermission(
      "SITE_MANAGEMENT",
      "WRITE",
    );
  }

  protected disableFormControls(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);

      if (control instanceof FormGroup) {
        this.disableFormControls(control);
      }

      control?.disable();
    });
  }

  subscribeFormChanges(form: FormGroup) {
    form.valueChanges.subscribe(() => {
      this.getPriceSignFormPristine();
    });
  }

  createPricePolePointFormGroup(pricePolePoint?: PricePolePoint): FormGroup {
    const pricePolePointGroup = this.fb.group({
      [PRICE_SIGN_FORM_PARAMS.pricePolePointID]: [
        pricePolePoint?.pricePolePointID ?? null,
        [Validators.required],
      ],
      [PRICE_SIGN_FORM_PARAMS.signNo]: [
        pricePolePoint?.signNo ?? null,
        [Validators.required],
      ],
      [PRICE_SIGN_FORM_PARAMS.manualMode]: [
        pricePolePoint?.manualMode ?? false,
      ],
      [PRICE_SIGN_FORM_PARAMS.segments]: this.fb.array([]),
    });
    const segments = pricePolePointGroup.get(
      PRICE_SIGN_FORM_PARAMS.segments,
    ) as FormArray;

    const segmentList = pricePolePoint?.segments ?? [undefined];

    segmentList.forEach((segment) => {
      segments.push(this.createSegmentFormGroup(segment));
    });

    return pricePolePointGroup;
  }

  createSegmentFormGroup(segment?: Segment): FormGroup {
    return this.fb.group({
      [PRICE_SIGN_FORM_PARAMS.segmentNo]: [
        segment?.segmentNo ?? null,
        [Validators.required],
      ],
      [PRICE_SIGN_FORM_PARAMS.productNo]: [
        segment?.productNo ?? null,
        [Validators.required],
      ],
      [PRICE_SIGN_FORM_PARAMS.modeNo]: [segment?.modeNo ?? 0],
    });
  }

  addPricePolePoint() {
    const pricePolePoints = this.getFormArray(
      PRICE_SIGN_FORM_PARAMS.pricePolePoints,
    );
    pricePolePoints.push(this.createPricePolePointFormGroup());
  }

  addSegment(index: number) {
    const segments = this.getSegmentsArray(index);
    segments.push(this.createSegmentFormGroup());
  }

  removePricePolePoint(index: number) {
    const pricePolePoints = this.getFormArray(
      PRICE_SIGN_FORM_PARAMS.pricePolePoints,
    );
    pricePolePoints.removeAt(index);
  }

  removeSegment(pricePolePointIndex: number, segmentIndex: number) {
    const segments = this.getSegmentsArray(pricePolePointIndex);
    segments.removeAt(segmentIndex);
  }

  moveSegmentUp(pricePolePointIndex: number, segmentIndex: number) {
    const segments = this.getSegmentsArray(pricePolePointIndex);
    const group = segments.at(segmentIndex);
    segments.removeAt(segmentIndex);
    segments.insert(segmentIndex - 1, group);
  }

  moveSegmentDown(pricePolePointIndex: number, segmentIndex: number) {
    const segments = this.getSegmentsArray(pricePolePointIndex);
    const group = segments.at(segmentIndex);
    segments.removeAt(segmentIndex);
    segments.insert(segmentIndex + 1, group);
  }

  getFormArray(formParam: string): FormArray {
    return this.priceSignDataForm.get(formParam) as FormArray;
  }

  getSegmentsArray(index: number) {
    return this.getFormArray(PRICE_SIGN_FORM_PARAMS.pricePolePoints).controls[
      index
    ].get(PRICE_SIGN_FORM_PARAMS.segments) as FormArray;
  }

  setPricePolePointsFormArray(priceSign: SitePriceSignDTO) {
    const pricePolePoints = this.getFormArray(
      PRICE_SIGN_FORM_PARAMS.pricePolePoints,
    );
    this.clearFormArray(pricePolePoints);

    priceSign.additionalData?.pricePolePoints?.forEach((pricePolePoint) => {
      pricePolePoints.push(this.createPricePolePointFormGroup(pricePolePoint));
    });
  }

  clearFormArray(formArray: FormArray) {
    formArray.clear();
  }

  setFormValues(priceSign: SitePriceSignDTO, duplication?: boolean) {
    this.priceSignDataForm.controls.enabled.setValue(
      priceSign?.enabled ?? false,
    );
    this.priceSignDataForm.controls.priceSignFccCode.setValue(
      duplication ? null : (priceSign?.priceSignFccCode ?? null),
    );
    this.priceSignDataForm.controls.serialNumber.setValue(
      duplication ? null : (priceSign?.serialNumber ?? null),
    );
    this.priceSignDataForm.controls.description.setValue(
      priceSign?.description ?? null,
    );
    this.priceSignDataForm.controls.protocolTypeCode.setValue(
      priceSign?.protocolTypeCode ?? null,
    );

    setProtocolFormValues(this.priceSignDataForm, priceSign);
  }

  duplicate() {
    this.setFormValues(this.currentPriceSign, true);

    this.isCreate = true;
    this.currentPriceSign = null;
  }

  onSubmit() {
    if (!this.priceSignDataForm.valid) {
      this.priceSignDataForm.markAllAsTouched();
      this.snackbarService.show({
        message: this.translate.instant("SiteManagement.formInvalid"),
        type: SnackbarType.ERROR,
      });
      return;
    }

    if (this.priceSignDataForm.pristine && !this.isCreate) {
      this.snackbarService.show({
        message: this.translate.instant("SiteManagement.NoChanges"),
        type: SnackbarType.WARNING,
      });
      return;
    }


    const protocolFormGroup = this.priceSignDataForm.get(
      PRICE_SIGN_FORM_PARAMS.protocol,
    ) as FormGroup;
    let priceSign: SitePriceSignDTO = {
      siteCode: this.siteCode ? this.siteCode : null,
      code: this.currentPriceSign?.code,
      enabled: this.priceSignDataForm.controls.enabled.value ?? false,
      priceSignFccCode:
        this.priceSignDataForm.controls.priceSignFccCode.value ?? null,
      serialNumber: this.priceSignDataForm.controls.serialNumber.value ?? null,
      description: this.priceSignDataForm.controls.description.value ?? null,
      protocolTypeCode:
        this.priceSignDataForm.controls.protocolTypeCode.value ?? null,
      communicationMethodTypeCode: protocolFormGroup.get(
        PROTOCOL_FORM_PARAMS.communicationMethodTypeCode,
      ).value
        ? CommunicationMethodTypeCode.SERIAL_CONNECTION
        : CommunicationMethodTypeCode.IP_PROTOCOL,
      communicationMethodData: setCommunicationMethodData(protocolFormGroup),
      additionalData: this.setAdditionalData(),
    };

    if (this.isCreate) {
      this.store.dispatch(createPriceSign({ priceSign }));
    } else {
      this.store.dispatch(updatePriceSign({ priceSign }));
    }

    this.priceSignDataForm.markAsUntouched();
    this.priceSignDataForm.markAsPristine();
    this.getPriceSignFormPristine();
  }

  setAdditionalData(): { pricePolePoints: PricePolePoint[] } {
    const pricePolePoints = [];
    const pricePolePointsFormArray = this.getFormArray(
      PRICE_SIGN_FORM_PARAMS.pricePolePoints,
    );
    pricePolePointsFormArray.controls.forEach((pricePolePoint, index) => {
      const segments = [];
      const segmentsFormArray = this.getSegmentsArray(index);
      segmentsFormArray.controls.forEach((segment) => {
        segments.push({
          segmentNo:
            segment.get(PRICE_SIGN_FORM_PARAMS.segmentNo).value ?? null,
          productNo:
            segment.get(PRICE_SIGN_FORM_PARAMS.productNo).value ?? null,
          modeNo: segment.get(PRICE_SIGN_FORM_PARAMS.modeNo).value ?? 0,
        });
      });
      pricePolePoints.push({
        pricePolePointID:
          pricePolePoint.get(PRICE_SIGN_FORM_PARAMS.pricePolePointID).value ??
          null,
        signNo: pricePolePoint.get(PRICE_SIGN_FORM_PARAMS.signNo).value ?? null,
        manualMode: pricePolePoint.get(PRICE_SIGN_FORM_PARAMS.manualMode).value
          ? "true"
          : "false",
        segments: segments,
      });
    });

    return { pricePolePoints };
  }

  getPriceSignFormPristine() {
    this.getPriceSignForm.emit(this.priceSignDataForm);
  }

  close() {
    this.dialogRef.close();
  }

  @HostListener("window:beforeunload")
  canDeactivate(): Observable<boolean> | boolean {
    return this.isNavigationAllowed();
  }

  isNavigationAllowed(): Observable<boolean> | boolean {
    if (!isAuthenticated(this.router.url) || this.priceSignDataForm.pristine) {
      return true;
    } else {
      return false;
    }
  }

  confirmDialog(dialog: any): Observable<boolean> {
    const dialogData: Dialog = GuardConfirmationDialogData;
    const dialogRef = dialog.open(ConfirmationDialogComponent, dialogData);
    return dialogRef.afterClosed();
  }
}
